import { AddOns, MemberType, Permissions } from "@nova/constants"
import { input, z, infer as ZodInfer } from "zod"
import { cognitoUserSchema } from "./cognito"
import { AddOnSchema, LicenseSchema } from "../licenses/licenses"
import { parseValidItems } from "../util"

const pkName = "organizationId"
const skName = "userId"

export const organizationPermissionsSchema = z.object({
    workspaceAccess: z.enum(Permissions).catch(Permissions.None),
    subRoles: z
        .array(
            z.object({
                id: z.string(),
                permission: z.enum(Permissions).catch(Permissions.None),
            })
        )
        .catch([]),
    accessRequestIds: z.array(z.string()).catch([]),
})

export type OrganizationPermissions = ZodInfer<typeof organizationPermissionsSchema>
export type OrganizationPermissionsInput = input<typeof organizationPermissionsSchema>

export const baseMemberRecordSchema = z.object({
    [pkName]: z.string(),
    [skName]: z.string(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    deletedAt: z.string().optional(),
    permissions: organizationPermissionsSchema.catch({
        workspaceAccess: Permissions.None,
        subRoles: [],
        accessRequestIds: [],
    }),
    favorites: z.array(z.string()).catch([]),
    recents: z.string().optional(),
    recentSearches: z.string().optional(),
})

export const registeredMemberRecordSchema = baseMemberRecordSchema.extend({
    type: z.literal(MemberType.Member),
})

export type RegisteredMemberRecord = z.infer<typeof registeredMemberRecordSchema>

export const inviteRecordSchema = baseMemberRecordSchema.extend({
    type: z.literal(MemberType.Invite),
})

export type InviteMemberRecord = z.infer<typeof inviteRecordSchema>

export const memberRecordSchema = z.discriminatedUnion("type", [
    registeredMemberRecordSchema,
    inviteRecordSchema,
])

export type MemberRecord = ZodInfer<typeof memberRecordSchema>

export const cognitoMemberSchema = registeredMemberRecordSchema.extend({
    ...cognitoUserSchema.shape,
})

export type CognitoMember = ZodInfer<typeof cognitoMemberSchema>

export const recentSearchEntrySchema = z.object({
    pluginName: z.string(),
    data: z.unknown(),
    timestamp: z.string(),
})

export type RecentSearchEntry = ZodInfer<typeof recentSearchEntrySchema>

export const parseRecentSearches = (raw: string | undefined): RecentSearchEntry[] => {
    if (!raw) return []
    try {
        return parseValidItems(recentSearchEntrySchema, JSON.parse(raw) as unknown[])
    } catch {
        return []
    }
}

export const memberExtendedSchema = cognitoMemberSchema.extend({
    addOns: z.record(z.enum(AddOns), z.union([AddOnSchema, z.null()])),
    license: LicenseSchema,
})

export type MemberExtended = z.infer<typeof memberExtendedSchema>

export const workspaceMemberSchema = z.discriminatedUnion("type", [
    memberExtendedSchema,
    inviteRecordSchema,
])

export type WorkspaceMember = ZodInfer<typeof workspaceMemberSchema>
